// PublicMenuPage.tsx - Uses menu styleConfig for fonts, colors, and design
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, FileText } from 'lucide-react'
import axios from '../../utils/axios';
import PublicOpeningHoursDisplay from '../../components/menu/PublicOpeningHoursDisplay';
import { 
  Loader2, Globe, ChevronDown, Phone, Mail, MapPin, ExternalLink,
  Facebook, Instagram, Star, Facebook as FacebookIcon, 
  Instagram as InstagramIcon, Star as TripAdvisorIcon,
  Globe as GoogleBusinessIcon
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { OPENAI_SUPPORTED_LANGUAGES, getLanguageByCode, getDefaultLanguage } from '../../utils/languages';
import { resolveStyleConfig, parseRawConfig, ResolvedStyleConfig, overlayColorToRgba } from '../../utils/publicMenuStyleUtils';
import { getAllergenDisplayNameItalian, getAllergenDescriptionItalian } from '../../utils/allergenDisplayNames';
import { getPublicAllergenIconPath } from '../../utils/publicAllergens';
import AllergenIcon from '../../components/menu/AllergenIcon';
import { sortMenuByPosition } from '../../utils/menuSortUtils';
import { usePublicMenuMeta } from '../../hooks/usePublicMenuMeta';

interface Allergen {
  id: number;
  name: string;
  code?: string;
  icon?: string;
  description?: string;
}

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price?: string | number;
  imageUrl?: string;
  isVegetarian?: boolean;
  isVegan?: boolean;
  isSpicy?: boolean;
  allergens?: Allergen[];
  Allergens?: Allergen[];
}

interface Category {
  id: number;
  name: string;
  description?: string;
  position?: number;
  sectionId?: number;
  items?: MenuItem[];
  MenuItems?: MenuItem[];
}

interface MenuSection {
  id: number;
  name: string;
  description?: string;
  position?: number;
  Categories?: Category[];
}

interface Restaurant {
  name: string;
  address?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  logoUrl?: string;
  socialLinks?: {
    tripadvisor?: string;
    google?: string;
    facebook?: string;
    instagram?: string;
  };
}

interface MenuData {
  id: number;
  title?: string;
  slug?: string;
  description?: string;
  language?: string;
  restaurant?: Restaurant;
  categories?: Category[];
  MenuSections?: MenuSection[];
  styleConfig?: Record<string, unknown> | string;
}

// Direct paths to your default background images
const DEFAULT_LIGHT_BACKGROUND = '/images/backgrounds/default-light-bg.jpg';
const DEFAULT_DARK_BACKGROUND = '/images/backgrounds/default-dark-bg.jpg';

// Helper function to get full URL for images
const getFullImageUrl = (imagePath: string | null | undefined): string => {
  if (!imagePath) return '';
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) {
    return imagePath;
  }
  
  // If it's a relative path starting with /uploads/, construct full URL
  if (imagePath.startsWith('/uploads/')) {
    const baseUrl = window.location.origin;
    return `${baseUrl}${imagePath}`;
  }
  
  // For other relative paths, add the base URL
  const baseUrl = window.location.origin;
  return `${baseUrl}/${imagePath}`;
};

// Cloudinary URL fix
const fixCloudinaryUrl = (url: string | null): string | null => {
  if (!url || !url.includes('cloudinary.com')) return url;
  
  try {
    const urlObj = new URL(url);
    
    // Add cache busting parameter
    urlObj.searchParams.append('_', Date.now().toString());
    
    // Ensure CORS-friendly format
    const pathname = urlObj.pathname;
    if (pathname.includes('/upload/')) {
      const parts = pathname.split('/upload/');
      if (parts.length === 2) {
        // Add transformations that are CORS-friendly
        const transformations = 'f_auto,q_auto:good,c_limit,w_1920';
        urlObj.pathname = `${parts[0]}/upload/${transformations}/${parts[1]}`;
      }
    }
    
    return urlObj.toString();
  } catch (error) {
    console.warn('Could not parse Cloudinary URL:', url, error);
    return url;
  }
};

// Determine background image from raw config (before resolveStyleConfig)
function determineBackgroundImage(cfg: any): string | null {
  if (cfg?.backgroundImage) return cfg.backgroundImage;
  const bg = (cfg?.background || cfg?.backgroundColor || '#ffffff').toLowerCase();
  const isWhite = bg === '#ffffff' || bg === '#fff' || bg === 'white';
  return isWhite ? DEFAULT_LIGHT_BACKGROUND : DEFAULT_DARK_BACKGROUND;
}

// Helper function to get all categories from menu data
const getAllCategories = (menuData: MenuData): Category[] => {
  const categories: Category[] = [];
  
  if (menuData.MenuSections) {
    menuData.MenuSections.forEach(section => {
      if (section.Categories) {
        section.Categories.forEach(category => {
          categories.push({
            ...category,
            items: category.items || category.MenuItems || []
          });
        });
      }
    });
  }
  
  if (menuData.categories) {
    menuData.categories.forEach(category => {
      categories.push({
        ...category,
        items: category.items || category.MenuItems || []
      });
    });
  }
  
  return categories;
};

// Allergen Modal Component - uses ResolvedStyleConfig
const AllergenModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  styleConfig: ResolvedStyleConfig;
  menuId: number;
}> = ({ isOpen, onClose, styleConfig, menuId }) => {
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [loading, setLoading] = useState(false);
  const isDarkTheme = styleConfig.theme === 'dark';

  useEffect(() => {
    if (isOpen && menuId) {
      fetchAllergens();
    }
  }, [isOpen, menuId]);

   const fetchAllergens = async () => {
    try {
      setLoading(true);
      // MODIFIED: Now using menuId in the API endpoint
      const response = await axios.get(`allergens/menu/${menuId}`);
      if (response.data.success) {
        setAllergens(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching allergens:', error);
      toast.error('Impossibile caricare le informazioni sugli allergeni');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-2xl flex flex-col"
        style={{ 
          backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
          color: isDarkTheme ? '#ffffff' : '#333333'
        }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 "
          style={{ borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)' }}
        >
          <h2 
            className="text-2xl font-bold"
            style={{ 
              fontFamily: styleConfig.fontFamily,
              color: isDarkTheme ? '#ffffff' : styleConfig.primaryColor
            }}
          >
            Lista allergeni
          </h2>
          <button
            onClick={onClose}
            className="text-2xl hover:opacity-70 transition-opacity"
            style={{ color: isDarkTheme ? '#ffffff' : '#333333' }}
            aria-label="Chiudi"
          >
            &times;
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: styleConfig.secondaryColor }} />
              <span className="ml-3">Caricamento allergeni...</span>
            </div>
          ) : allergens.length === 0 ? (
            <div className="text-center py-12">
              <p>Nessuna informazione sugli allergeni disponibile.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {allergens.map((allergen) => (
                <div 
                  key={allergen.id}
                  className="p-4 rounded-lg"
                  style={{ 
                    backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                    border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <AllergenIcon allergen={allergen} alt={getAllergenDisplayNameItalian(allergen)} className="w-6 h-6" />
                    <h3 
                      className="text-lg font-semibold"
                      style={{ fontFamily: styleConfig.fontFamily }}
                    >
                      {getAllergenDisplayNameItalian(allergen)}
                    </h3>
                    {allergen.code && (
                      <span 
                        className="text-sm px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {allergen.code}
                      </span>
                    )}
                  </div>
                  {(() => {
                    const desc = getAllergenDescriptionItalian(allergen);
                    return desc ? <p className="mt-2 opacity-80">{desc}</p> : null;
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div 
          className="p-4 border-t text-center font-800 font-bold">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-md font-medium hover:opacity-90 transition-opacity"
            style={{ 
              backgroundColor: styleConfig.secondaryColor,
              color: '#ffffff'
            }}
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

const PublicMenuPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>(searchParams.get('lang') || 'it');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [styleConfig, setStyleConfig] = useState<ResolvedStyleConfig>(() => resolveStyleConfig(undefined));
  const [categoryDisplayMode, setCategoryDisplayMode] = useState<'cloud' | 'scroll'>('cloud');
  const [showAllergenModal, setShowAllergenModal] = useState(false);
  const [visibleAllergenKey, setVisibleAllergenKey] = useState<string | null>(null);
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);
  const [backgroundImageError, setBackgroundImageError] = useState(false);
  const [pageKey, setPageKey] = useState(0);

  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = getLanguageByCode(selectedLanguage) || getDefaultLanguage();

  usePublicMenuMeta(menuData?.restaurant?.name);

  // Load and test background image
  useEffect(() => {
    if (styleConfig.backgroundImage) {
      setBackgroundImageLoaded(false);
      setBackgroundImageError(false);
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.referrerPolicy = 'no-referrer';
      
      img.onload = () => {
        setBackgroundImageLoaded(true);
        setBackgroundImageError(false);
      };
      
      img.onerror = () => {
        setBackgroundImageError(true);
        setBackgroundImageLoaded(false);
      };
      
      // Fix Cloudinary URL for CORS
      let imageUrl = styleConfig.backgroundImage;
      if (imageUrl.includes('cloudinary.com')) {
        const fixedUrl = fixCloudinaryUrl(imageUrl);
        if (fixedUrl) imageUrl = fixedUrl;
      }
      
      img.src = imageUrl;
    } else {
      setBackgroundImageLoaded(false);
      setBackgroundImageError(false);
    }
  }, [styleConfig.backgroundImage]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize default selections when menu data loads
  useEffect(() => {
    if (menuData) {
      const sections = menuData.MenuSections || [];
      const allCategories = getAllCategories(menuData);
      
      if (sections.length > 0) {
        const firstSection = sections[0];
        setSelectedSection(firstSection.name);
        
        if (firstSection.Categories && firstSection.Categories.length > 0) {
          const firstCategory = firstSection.Categories[0];
          setSelectedCategory(firstCategory.name);
        }
      } else if (allCategories.length > 0) {
        setSelectedCategory(allCategories[0].name);
      }
    }
  }, [menuData]);

  // Auto-select first category when section changes
  useEffect(() => {
    if (menuData && selectedSection) {
      const sections = menuData.MenuSections || [];
      const currentSection = sections.find(s => s.name === selectedSection);
      
      if (currentSection?.Categories && currentSection.Categories.length > 0) {
        const firstCategory = currentSection.Categories[0];
        
        // Only auto-select if no category is currently selected
        // OR if the current selected category doesn't belong to this section
        const currentSectionCategories = currentSection.Categories || [];
        const isCurrentCategoryInSection = currentSectionCategories.some(
          cat => cat.name === selectedCategory
        );
        
        if (!selectedCategory || !isCurrentCategoryInSection) {
          setSelectedCategory(firstCategory.name);
        }
      } else {
        // If the section has no categories, clear the selection
        setSelectedCategory(null);
      }
    }
  }, [selectedSection, menuData]); // Removed selectedCategory from dependencies

  // Initialize category display mode from styleConfig
  useEffect(() => {
    if (styleConfig.categoryDisplayMode) {
      setCategoryDisplayMode(styleConfig.categoryDisplayMode);
    }
  }, [styleConfig]);


  useEffect(() => {
    if (slug) {
      const langFromUrl = searchParams.get('lang');
      if (langFromUrl) {
        setSelectedLanguage(langFromUrl);
        fetchMenuData(langFromUrl);
      } else {
        fetchMenuData(selectedLanguage);
      }
    }
  }, [slug]);

  const fetchMenuData = async (lang: string, forceRetranslate = false, retryCount = 0) => {
    try {
      setLoading(true);
      setError('');
      const params: Record<string, string> = { lang };
      if (forceRetranslate || searchParams.get('retranslate') === '1') params.retranslate = '1';
      const response = await axios.get(`/menu/public/${slug}`, { params });
      
      if (response.data.success) {
        const data: MenuData = sortMenuByPosition(response.data.data) as MenuData;
        setMenuData(data);
        
        if (data.language && !selectedLanguage) {
          setSelectedLanguage(data.language);
        }
        
        // Resolve style config from menu settings (fonts, colors, sizes)
        const resolved = resolveStyleConfig(data.styleConfig);
        const rawCfg = parseRawConfig(data.styleConfig);
        const bgImage = resolved.backgroundImage || determineBackgroundImage(rawCfg);
        const finalConfig: ResolvedStyleConfig = {
          ...resolved,
          backgroundImage: bgImage,
          categoryDisplayMode: resolved.categoryDisplayMode || 'cloud'
        };
        setStyleConfig(finalConfig);
        if (finalConfig.categoryDisplayMode) {
          setCategoryDisplayMode(finalConfig.categoryDisplayMode);
        }

        // Backend returned Italian while translating in background; retry once after delay to get translated menu
        const pendingLang = response.headers?.['x-translation-pending'];
        if (pendingLang && retryCount < 2) {
          setTimeout(() => fetchMenuData(lang, forceRetranslate, retryCount + 1), 3500);
        }
      } else {
        setError('Menu not found');
      }
    } catch (error: any) {
      console.error('Error fetching menu:', error);
      setError(error.response?.data?.message || 'Failed to load menu');
    } finally {
      setLoading(false);
      setTranslating(false);
    }
  };

  const handleLanguageChange = async (langCode: string, e?: React.MouseEvent) => {
    if (langCode === selectedLanguage) {
      setIsLanguageDropdownOpen(false);
      return;
    }
    const forceRetranslate = e?.altKey === true;
    setTranslating(true);
    setSelectedLanguage(langCode);
    setIsLanguageDropdownOpen(false);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('lang', langCode);
    if (forceRetranslate) newUrl.searchParams.set('retranslate', '1');
    else newUrl.searchParams.delete('retranslate');
    window.history.pushState({}, '', newUrl.toString());
    await fetchMenuData(langCode, forceRetranslate);
  };

  // Filter out "all categories" entry and get categories for display
  const getDisplayCategories = () => {
    const sections = menuData?.MenuSections || [];
    const allCategories = menuData ? getAllCategories(menuData) : [];
    
    let categories: Category[] = [];
    
    if (sections.length > 0 && selectedSection) {
      const section = sections.find(s => s.name === selectedSection);
      if (section?.Categories) {
        categories = section.Categories.filter(cat => 
          !cat.name.toLowerCase().includes('all categories') && 
          !cat.name.toLowerCase().includes('tutte le categorie') &&
          !cat.name.toLowerCase().includes('tutti')
        );
      }
    } else {
      categories = allCategories.filter(cat => 
        !cat.name.toLowerCase().includes('all categories') && 
        !cat.name.toLowerCase().includes('tutte le categorie') &&
        !cat.name.toLowerCase().includes('tutti')
      );
    }
    
    return categories;
  };

  // Get background styles - memoized to prevent blink on tab switch
  const hasBackgroundImage = !!(styleConfig.backgroundImage && backgroundImageLoaded && !backgroundImageError);
  const overlayOpacity = styleConfig.overlayOpacity ?? 0;

  const backgroundStyles = useMemo(() => {
    const baseStyles: React.CSSProperties = {
      minHeight: '100vh',
      position: 'relative',
      zIndex: 1
    };

    if (styleConfig.backgroundImage && backgroundImageLoaded && !backgroundImageError) {
      let imageUrl = styleConfig.backgroundImage;
      if (imageUrl.includes('cloudinary.com')) {
        const fixedUrl = fixCloudinaryUrl(imageUrl);
        if (fixedUrl) imageUrl = fixedUrl;
      }
      return {
        ...baseStyles,
        backgroundColor: styleConfig.backgroundColor,
        backgroundImage: `url("${imageUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      };
    }
    // No image: use overlay color as background instead of Colore Sfondo when overlay is configured
    if (overlayOpacity > 0) {
      const overlayColor = styleConfig.overlayColor ?? '#1a1a1a';
      return {
        ...baseStyles,
        backgroundColor: /^#[0-9A-Fa-f]{3,8}$/.test(overlayColor) ? overlayColor : '#1a1a1a'
      };
    }
    return {
      ...baseStyles,
      backgroundColor: styleConfig.backgroundColor
    };
  }, [styleConfig.backgroundColor, styleConfig.backgroundImage, styleConfig.overlayColor, overlayOpacity, backgroundImageLoaded, backgroundImageError]);

  // Handle image errors with fallback
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, imageUrl: string) => {
    console.error('Image failed to load:', imageUrl);
    e.currentTarget.style.display = 'none';
    
    // Try with CORS proxy for Cloudinary images
    if (imageUrl.includes('cloudinary.com')) {
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(imageUrl)}`;
      const img = new Image();
      img.onload = () => {
        e.currentTarget.src = proxyUrl;
        e.currentTarget.style.display = 'block';
      };
      img.src = proxyUrl;
    }
  };

  if (loading && !menuData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error || !menuData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Menu Not Available</h1>
          <p className="text-gray-600 mb-6">
            {error || 'This menu is currently unavailable.'}
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white hover:bg-gray-900 transition"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  const sections = menuData.MenuSections || [];
  const displayCategories = getDisplayCategories();
  const restaurant = menuData.restaurant;
  const socialLinks = restaurant?.socialLinks || {};
  
  // Determine text color based on theme
  const isDarkTheme = styleConfig.theme === 'dark';
  const headerFooterTextColor = isDarkTheme ? '#ffffff' : styleConfig.textColor;
  const headerFooterBorderColor = isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)';
  const linkTextColor = isDarkTheme ? '#ffffff' : 'inherit';
  
  // Current menu URL
  const menuUrl = window.location.href;
  
  // Get restaurant logo URL
  const rawLogoUrl = restaurant?.logoUrl || styleConfig.logoUrl;
  const restaurantLogoUrl = rawLogoUrl ? getFullImageUrl(rawLogoUrl) : '';
  const logoSize = styleConfig.logoSize || 'medium';
  const logoHeightHeader = { small: 32, medium: 40, large: 56 }[logoSize] ?? 40;
  const logoHeightFooter = { small: 40, medium: 52, large: 72 }[logoSize] ?? 52;
  const showOverlay = overlayOpacity > 0;
  
  return (
    <div 
      className="min-h-screen relative"
        style={{
          ...backgroundStyles,
          fontFamily: styleConfig.fontFamily,
          color: styleConfig.textColor,
          fontSize: styleConfig.fontSizeBase,
        lineHeight: styleConfig.lineHeight,
        position: 'relative',
        zIndex: 1,
        minHeight: '100vh'
      }}
    >
      {/* Overlay layer - above background image, below content */}
      {showOverlay && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            zIndex: 2,
            background: overlayColorToRgba(styleConfig.overlayColor ?? '#000000', overlayOpacity)
          }}
          aria-hidden
        />
      )}
      {/* Content container */}
      <div className="relative z-10">
        {/* Header */}
        <header className="">
          <div className="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Restaurant Logo and Name - Left side */}
              <div className="flex items-center gap-3 flex-shrink-0 max-w-[70%]">
                {restaurantLogoUrl && (
                  <img
                    src={restaurantLogoUrl}
                    alt={restaurant?.name || 'Restaurant'}
                    className="w-auto object-contain"
                    style={{ height: logoHeightHeader }}
                    onError={(e) => handleImageError(e, restaurantLogoUrl)}
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                  />
                )}
               
              </div>
              
              {/* Language Selector - Right side */}
              <div className="relative flex-shrink-0" ref={languageDropdownRef}>
  <button
    onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
    className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 
               border border-gray-300 hover:border-gray-400 
               transition-colors rounded-lg"
    style={{ 
      color: headerFooterTextColor,
      backgroundColor: isDarkTheme ? 'rgba(0, 0, 0, 0.8)' : '#ffffff',
      borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
    }}
    disabled={translating}
  >
    {translating ? (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Load...</span>
      </>
    ) : (
      <>
        {/* ✅ FLAG */}
        <span className="text-xl leading-none">
          {currentLanguage.flag}
        </span>

        {/* Language name (hidden on mobile) */}
        <span className="text-sm font-medium hidden md:inline">
          {currentLanguage.nativeName}
        </span>

        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            isLanguageDropdownOpen ? 'rotate-180' : ''
          }`}
          style={{ color: headerFooterTextColor }}
        />
      </>
    )}
  </button>

  {/* ================= DROPDOWN ================= */}
  {isLanguageDropdownOpen && !translating && (
    <div
      className="
        absolute right-0 mt-1 w-52 md:w-64
        border border-gray-200 shadow-lg rounded-lg z-50
      "
      style={{ 
        backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
        color: headerFooterTextColor,
        borderColor: headerFooterBorderColor
      }}
    >
      {/* ✅ FIXED HEIGHT + SCROLL */}
      <div className="max-h-64 overflow-y-auto py-2">
        {OPENAI_SUPPORTED_LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={(e) => handleLanguageChange(lang.code, e)}
            title={lang.code !== 'it' ? 'Alt+click to force re-translation' : undefined}
            className={`
              w-full flex items-center gap-3 px-3 py-2
              hover:bg-gray-50 transition-colors
              ${selectedLanguage === lang.code ? 'bg-gray-50' : ''}
            `}
            style={{ 
              color: headerFooterTextColor,
              backgroundColor: selectedLanguage === lang.code ? (isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)') : 'transparent'
            }}
          >
            {/* FLAG */}
            <span className="text-2xl leading-none">
              {lang.flag}
            </span>

            <div className="flex-1 text-left">
              <div className="font-medium text-sm md:text-base">
                {lang.nativeName}
              </div>
              <div 
                className="text-xs hidden md:block"
                style={{ color: isDarkTheme ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
              >
                {lang.name}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )}
</div>

            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Macro Category (Section) Navigation */}
          {sections.length > 1 && (
            <div className="">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div 
                  className="flex overflow-x-auto py-2"
                  style={{
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                  }}
                >
                  <style>
                    {`
                      .scroll-hide::-webkit-scrollbar {
                        display: none;
                      }
                    `}
                  </style>
                  <div className="scroll-hide flex justify-center md:justify-start">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => {
                          setPageKey(prev => prev + 1);
                          setSelectedSection(section.name);
                        }}
                        className={`flex-shrink-0 px-4 py-2 md:px-6 md:py-3 font-medium transition-all duration-200 whitespace-nowrap border-b-4 ${
                          selectedSection === section.name ? 'font-bold' : ''
                        }`}
                        style={{
                          color: selectedSection === section.name 
                            ? (isDarkTheme ? '#ffffff' : styleConfig.headerColor) 
                            : (isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : `${styleConfig.headerColor}99`),
                          fontSize: styleConfig.fontSizeMacroSection ?? styleConfig.fontSizeXLarge,
                          fontFamily: styleConfig.fontFamilyMacroSection ?? styleConfig.fontFamily,
                          borderBottomColor: selectedSection === section.name ? styleConfig.secondaryColor : 'transparent'
                        }}
                      >
                        {section.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Category Navigation */}
          {displayCategories.length > 0 && (
            <div className="px-4 md:px-6 py-3">
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {displayCategories.map((category) => {
                  const isActive = selectedCategory === category.name;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        if (selectedCategory !== category.name) {
                          setPageKey(prev => prev + 1);
                          setSelectedCategory(category.name);
                        }
                      }}
                      className={`px-4 py-2 font-medium transition-all duration-200 rounded-lg whitespace-nowrap ${
                        isActive ? 'shadow-sm' : ''
                      }`}
                      style={{
                        backgroundColor: isActive 
                          ? styleConfig.secondaryColor 
                          : (isDarkTheme ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)'),
                        color: isActive ? '#ffffff' : (isDarkTheme ? styleConfig.textColor : styleConfig.categoryColor),
                        fontSize: styleConfig.fontSizeCategory ?? styleConfig.fontSizeSmall,
                        fontFamily: styleConfig.fontFamilyCategory ?? styleConfig.fontFamily
                      }}
                    >
                      {category.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div style={{ position: 'relative', minHeight: '200px', overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={pageKey}
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{
                  type: 'spring',
                  stiffness: 260,
                  damping: 30
                }}
                className="space-y-6 md:space-y-8"
              >
            {(() => {
              // Find the selected category
              const selectedCat = displayCategories.find(cat => cat.name === selectedCategory);
              const items = selectedCat?.items || selectedCat?.MenuItems || [];
              
              if (items.length === 0) {
                return (
                  <div className="text-center py-12">
                    <p className="text-lg" style={{ color: styleConfig.descriptionColor }}>Nessun elemento trovato in questa categoria.</p>
                  </div>
                );
              }
              
              return items.map((item) => (
                <div
                  key={item.id}
                  className="pb-2 md:pb-1"
                  style={{
                    border: styleConfig.showItemBorder ? `${styleConfig.cardBorderWidth || '1px'} solid ${styleConfig.cardBorderColor}` : 'none',
                    borderRadius: styleConfig.borderRadius,
                    padding: '1rem 2rem 1rem 1rem'
                  }}
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Item Image */}
                    {styleConfig.showImages && item.imageUrl && (
                      <div className="md:w-1/3">
                        <img
                          src={getFullImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="w-full h-48 object-cover rounded-md"
                          onError={(e) => handleImageError(e, getFullImageUrl(item.imageUrl || ''))}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          loading="lazy"
                        />
                      </div>
                    )}
                    
                    {/* Item Details */}
                    <div className={`flex-1 ${styleConfig.showImages && item.imageUrl ? 'md:w-2/3' : ''}`}>
                      <div className="flex items-center gap-2 w-full">
                        <h3 
                          className="flex-[0_0_80%] min-w-0 break-words"
                          style={{ color: styleConfig.dishColor, fontSize: styleConfig.fontSizeDish ?? styleConfig.fontSizeLarge, fontFamily: styleConfig.fontFamilyDish ?? styleConfig.fontFamily, fontWeight: styleConfig.fontWeightDish ?? '700' }}
                        >
                          {item.name}
                        </h3>
                        <div 
                          className="flex-1 min-w-[8px] border-b border-dotted flex-shrink-0"
                          style={{ borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.3)' : styleConfig.cardBorderColor }}
                        />
                        {styleConfig.showPrices && item.price && (
                          <span 
                            className="whitespace-nowrap flex-[0_0_20%] text-right"
                            style={{ color: styleConfig.priceColor, fontSize: styleConfig.fontSizePrice ?? styleConfig.fontSizeDish ?? styleConfig.fontSizeLarge, fontFamily: styleConfig.fontFamilyPrice ?? styleConfig.fontFamilyDish ?? styleConfig.fontFamily, fontWeight: styleConfig.fontWeightPrice ?? '700' }}
                          >
                            {typeof item.price === 'number' ? `€${item.price.toFixed(2)}` : item.price}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p 
                          className="mt-1"
                          style={{ color: styleConfig.descriptionColor, fontSize: styleConfig.fontSizeDescription ?? styleConfig.fontSizeMedium, lineHeight: styleConfig.lineHeight, fontFamily: styleConfig.fontFamilyDescription ?? styleConfig.fontFamily }}
                        >
                          {item.description}
                        </p>
                      )}
                      {styleConfig.showAllergens && (item.allergens || item.Allergens) && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1 items-center">
                            {(item.allergens || item.Allergens)?.map((allergen) => {
                              const allergenKey = `${item.id}-${allergen.id}`;
                              const isNameVisible = visibleAllergenKey === allergenKey;
                              const displayName = getAllergenDisplayNameItalian(allergen);
                              return (
                                <span
                                  key={allergen.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setVisibleAllergenKey(isNameVisible ? null : allergenKey)}
                                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVisibleAllergenKey(isNameVisible ? null : allergenKey); } }}
                                  className="inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded cursor-pointer select-none hover:opacity-90"
                                  style={{
                                    fontSize: styleConfig.fontSizeSmall,
                                    color: styleConfig.allergenColor,
                                    backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : `${styleConfig.allergenColor}15`
                                  }}
                                  title={displayName}
                                >
                                  <AllergenIcon allergen={allergen} alt={displayName} className="w-5 h-5" invert={isDarkTheme} />
                                  {!getPublicAllergenIconPath(allergen.code) && !allergen.icon && displayName}
                                  {(getPublicAllergenIconPath(allergen.code) || allergen.icon) && isNameVisible && displayName && (
                                    <span className="font-medium whitespace-nowrap" style={{ fontSize: styleConfig.fontSizeSmall, color: styleConfig.priceColor }}>
                                      {displayName}
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ));
            })()}
              </motion.div>
            </AnimatePresence>
          </div>

        </main>

      
<footer className="mt-12 py-8" style={{ color: styleConfig.textColor, fontFamily: styleConfig.fontFamilyFooter ?? styleConfig.fontFamily, fontSize: styleConfig.fontSizeFooter ?? styleConfig.fontSizeBase }}>
  <div className="max-w-5xl mx-auto px-4">
    <div className="flex flex-col items-center text-center gap-6 md:gap-8">
      
      {/* Restaurant Logo - Moved to top */}
      {restaurantLogoUrl && (
        <img
          src={restaurantLogoUrl}
          alt={restaurant?.name}
          className="w-auto object-contain"
          style={{ height: logoHeightFooter }}
        />
      )}

      {/* Restaurant Website */}
      {restaurant?.website && (
        <a
          href={restaurant.website.startsWith('http') ? restaurant.website : `http://${restaurant.website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline hover:opacity-80 break-all"
          style={{ color: styleConfig.secondaryColor }}
        >
          {restaurant.website.replace(/^https?:\/\//, '')}
        </a>
      )}

      {/* Restaurant Contact Information */}
      <div className="text-center space-y-1">
        {restaurant?.name && (
          <h3 className="text-lg font-semibold" style={{ color: styleConfig.headerColor, fontFamily: styleConfig.fontFamilyFooter ?? styleConfig.fontFamily, fontSize: styleConfig.fontSizeFooter ?? styleConfig.fontSizeBase }}>{restaurant.name}</h3>
        )}
        {restaurant?.address && (
          <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>{restaurant.address}</p>
        )}
        {(restaurant?.city || restaurant?.country) && (
          <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>
            {restaurant.city}{restaurant.city && restaurant.country ? ', ' : ''}{restaurant.country}
          </p>
        )}
        {restaurant?.phone && (
          <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>
            <Phone className="w-4 h-4 inline mr-1" />
            {restaurant.phone}
          </p>
        )}
        {restaurant?.email && (
          <p className="text-sm" style={{ color: styleConfig.descriptionColor }}>
            <Mail className="w-4 h-4 inline mr-1" />
            {restaurant.email}
          </p>
        )}
      </div>

   

      {/* Social Icons - only show when at least one link is set */}
      {(socialLinks.tripadvisor || socialLinks.google || socialLinks.facebook || socialLinks.instagram) && (
        <div className="flex gap-4 md:gap-6" style={{ color: styleConfig.textColor }}>
          {socialLinks.tripadvisor && (
            <a href={socialLinks.tripadvisor} target="_blank" rel="noopener noreferrer" className="hover:opacity-70" style={{ color: 'inherit' }}>
              <Star className="w-5 h-5 md:w-6 md:h-6" />
            </a>
          )}
          {socialLinks.google && (
            <a href={socialLinks.google} target="_blank" rel="noopener noreferrer" className="hover:opacity-70" style={{ color: 'inherit' }}>
              <div className="w-5 h-5 md:w-6 md:h-6">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
                </svg>
              </div>
            </a>
          )}
          {socialLinks.facebook && (
            <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-70" style={{ color: 'inherit' }}>
              <FacebookIcon className="w-5 h-5 md:w-6 md:h-6" />
            </a>
          )}
          {socialLinks.instagram && (
            <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="hover:opacity-70" style={{ color: 'inherit' }}>
              <InstagramIcon className="w-5 h-5 md:w-6 md:h-6" />
            </a>
          )}
        </div>
      )}

      {/* Allergen List */}
      <button
        onClick={() => setShowAllergenModal(true)}
        className="text-sm md:text-base hover:underline font-medium"
        style={{ color: styleConfig.textColor }}
      >
        Lista allergeni e specifiche
      </button>

      {/* Privacy and Terms */}
     <div className="flex flex-col items-center justify-center w-full gap-3 md:flex-row md:gap-6 text-xs md:text-sm" style={{ color: styleConfig.descriptionColor }}>
        <a
          href={`/privacy-policy${slug ? `/${slug}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 hover:underline transition-colors"
          style={{ color: 'inherit' }}
        >
          <Lock className="w-4 h-4" />
          Privacy and Cookie Policy
        </a>
        <a
          href={`/terms-and-conditions${slug ? `/${slug}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 hover:underline transition-colors"
          style={{ color: 'inherit' }}
        >
          <FileText className="w-4 h-4" />
          Terms and Conditions
        </a>
      </div>

      {/* KMZERO Logo */}
      <div className="flex items-center justify-center">
        <img
          src={isDarkTheme ? "/images/kmzero-logo-dark.png" : "/images/kmzero-logo.png"}
          alt="KM ZERO Web Marketing"
          className="h-3 md:h-6 object-contain"
        />
      </div>

      {/* Powered by */}
      <p className="text-xs opacity-70 mt-0" style={{ color: styleConfig.descriptionColor }}>
        Menù Digitale® è un prodotto Km Zero Web Marketing
      </p>
    </div>
  </div>
</footer>

      </div>

      {/* Allergen Modal - MODIFIED: Pass menuId */}
      <AllergenModal
        isOpen={showAllergenModal}
        onClose={() => setShowAllergenModal(false)}
        styleConfig={styleConfig}
        menuId={menuData.id} // Pass the menuId from menuData
      />

    </div>
  );
};

export default PublicMenuPage;